import Vue from 'vue'
import Vuex from 'vuex'
import router from "../router/index"
import { flightStatus } from "../core/enum"
import { nomineeTypeMap } from "../core/enum"
import { sso, staff } from "../core/api/index"
import persistedState from 'vuex-persistedstate'
import { getFullName, transformBookings } from "../core/staff-helper"

Vue.use(Vuex)

export default new Vuex.Store({
  plugins: [persistedState({ storage: window.sessionStorage })],
  state: {
    token: '',
    loadingTask: null,
    // 載入中計數
    loadingCount: 0,
    // 登入資訊
    userInfo: null,
    // google 登入資訊
    guToken: null,
    guExpiry: null,
    googleUser: null,
    messagePopup: {
      title: null,
      message: null
    },
    // 去回站
    OD: {},
    countries: {},
    // 訂單資料
    bookings: [],
    // 朋友、親屬
    nominees: [],
    // 財政年
    financialYear: null,
    financialYears: [],
    // nominee 類別的用量資訊
    availableNominees: {},
    // 權利設定
    entitlementSettings: {},
    // 新增 nominee 時, 是否要跳出通知
    showAddNomineeNotice: true
  },
  getters: {
    loading: s => s.loadingCount > 0,
    staffId: s => s.userInfo?.staffId ?? '',
    userName: s => s.userInfo?.fullName ?? '',
    upcomingBookings: s => {
      let currentDate = new Date();
      return s.bookings?.filter(b => b.std >= currentDate).sort((a, b) => (b.children?.length ?? 0) - (a.children?.length ?? 0)).sort((a, b) => a.std - b.std) ?? [];
    },
    selectableBookings: s => {
      let currentDate = new Date();
      let usr = s.userInfo;
      return s.bookings?.filter(b => {
        if (b.parentPnr || b.std <= currentDate) {
          return false;
        }
        if (!b.passengers || b.passengers.length <= 0) {
          return false;
        }
        if (b.passengers.findIndex(p =>
          usr.lastName === p.lastname &&
          usr.firstName === p.firstname &&
          usr.dob.substring(0, 10) === p.dob.substring(0, 10)) === -1) {
          return false;
        }
        return true;
      }).sort((a, b) => a.std - b.std) ?? [];
    }
  },
  mutations: {
    setToken(s, token) {
      s.token = token;
    },
    setUser(s, usr) {
      s.userInfo = usr;
    },
    setLoadingCount(s, count) {
      s.loadingCount = Math.max(s.loadingCount + count, 0);
      const maxLoadingMillionSeconds = 60 * 1000;
      if (s.loadingTask != null) {
        clearTimeout(s.loadingTask);
      }
      s.loadingTask = setTimeout(() => s.loadingCount = 0, maxLoadingMillionSeconds);
    },
    setBookings(s, bookings) {
      s.bookings = transformBookings(bookings);
    },
    setEntitlement(s, entitlement) {
      s.entitlementSettings = entitlement;
    },
    setMessagePopup(s, info) {
      s.messagePopup = info;
    },
    setOD(s, od) {
      s.OD = od;
    },
    setCountries(s, countries) {
      s.countries = countries;
    },
    setGoogleUser(s, gu) {
      s.googleUser = gu;
    },
    setFinancialYears(s, years) {
      s.financialYears = years;
      let financialYear = years[0];
      if (years.length > 1) {
        const divide = new Date(`${years[0]}-04-01`);
        const currentDate = new Date();
        if (currentDate < divide) financialYear = years[1];
      }
      s.financialYear = financialYear;
    },
    setFinancialYear(s, year) {
      s.financialYear = year;
    },
    setNominees(s, nominees) {
      s.nominees = nominees.map(n => {
        n.financialYear = new Date(n.startDate).getFullYear();
        return n;
      });
    },
    setAvailableNominees(s, { data, year }) {
      let obj = {};
      data.forEach(x => {
        obj[x.nomineeType] = x;
        obj[x.nomineeType].displayName = nomineeTypeMap[x.nomineeType]
      })
      s.availableNominees[year] = obj;
    },
    updateNominee(s, { id, passportExp, passportNo }) {
      let index = s.nominees.findIndex(n => n.uuid === id);
      if (index !== -1) {
        s.nominees[index].expiryDate = passportExp;
        s.nominees[index].passportNumber = passportNo;
      }
    },
    deleteNominee(s, uuid) {
      let index = s.nominees.findIndex(n => n.uuid === uuid);
      if (index !== -1) {
        s.nominees.splice(index, 1);
      }
    },
    setNomineeNotice(s, status) {
      s.showAddNomineeNotice = status;
    },
    setGoogleInfo(s, gu) {
      s.guToken = gu.id_token;
      s.guExpiry = gu.expires_at;
    }
  },
  actions: {
    async login({ state, commit, dispatch }, googleAPI) {
      if (!googleAPI) { return; }
      let gu = null;
      let token = '';
      let idToken = state.guToken;
      let stamp = new Date().getTime();
      if (!idToken || !state.guExpiry || (state.guExpiry && state.guExpiry < stamp)) {
        gu = await window.gapi.auth2.getAuthInstance().signIn().then(r => r.getAuthResponse());
        idToken = gu.id_token;
        commit('setGoogleInfo', gu);
      }
      await sso.login(idToken).then(r => token = r.data.token);
      commit('setToken', token);
      dispatch('getAvailableNominees');
      await dispatch('getUser');
      router.push('/home')
    },
    logout: ({ state, commit }) => {
      if (state.token) {
        sso.logout(state.token);
        commit('setToken', '');
        router.push("/login");
      }
    },
    getAllBookings: ({ commit }) =>
      staff.getBookings({
        page: 1,
        pageSize: 100,
        flightStatus: flightStatus.all
      }).then(r => commit('setBookings', r.data.pageItems)),
    getBookings: (_, args) =>
      staff.getBookings(args).then(r => r.data.pageItems),
    getNominees: ({ commit }) =>
      staff.getNominees().then(r => commit('setNominees', r.data)),
    getUser: async ({ commit }) =>
      await staff.getStaff().then(r => {
        r.data.fullName = getFullName(r.data);
        commit('setUser', r.data);
      }),
    setMessagePopup: ({ commit }, msgObj) =>
      commit('setMessagePopup', msgObj),
    getAvailableNominees: ({ state, commit }) =>
      state.financialYears.forEach(x =>
        staff.getSummary(x).then(r => commit('setAvailableNominees', { year: x, data: r.data }))),
  },
  modules: {}
})
